import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";

// components
import PromptCardsV2 from "../PromptCardsV2";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";

// design system
import { Modal, SideNavigation, Button } from "@toddle-edu/ds-web";
import { colors } from "@toddle-edu/ds-theme";

// modules
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getAIPromptsQuery } from "../../modules/LessonPlanningQueries";
import {
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";

// third party
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";

// utils
import {
  getAITutorSidebarOptions,
  getPromptsForSuggestionLibrary,
} from "../../modules/Utils";
import { wrapPlaceholderWithSpan } from "../PromptCards/PromptCards";
import { getPromptLabelsObj } from "Utils/AIUtils";
import { AI_TUTOR_LEARNING_TOOLS } from "@toddle-edu/common-utils";
import { getIsAITutorInEEEnabled } from "Utils/ACLStoreUtils";

// scss
import classes from "./AITutorLibraryV2.scss";

const source = "LIBRARY";

const styles = {
  modalBodyStyle: {
    padding: "0px",
    display: "flex",
    overflow: "hidden",
  },
  aiTutorChatContainerStyle: {
    backgroundColor: colors.surfaceSubtle,
  },
  sideNavigationStyle: {
    overflowY: "auto",
  },
  headerStyle: {
    margin: "0px",
  },
};

const AITutorLibraryV2 = props => {
  const {
    t,
    onPromptSuggestionModalClose: onClose,
    prompts,
    flattenedPrompts,
    assessmentDetails,
    setSelectedAction,
    leId,
    gradeId,
    assessmentTemplate,
    options,
    initialOption = null,
    initialGroupType,
    isLoading,
    isData,
    i18n,
    setSelectedLearningTool,
  } = props;

  // ------------------ states -----------------------
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedChildPrompt, setSelectedChildPrompt] = useState(null);
  const [activeSubject, setActiveSubject] = useState(initialOption);

  const promptsContainerRef = useRef(null);
  const subjectSectionRefs = useRef({});
  const isSubjectClickRef = useRef(false);

  // ------------------ side effects -----------------------

  useEffect(() => {
    if (initialGroupType) {
      const selectedPrompt = _.find(
        flattenedPrompts,
        ({ id }) => id === initialGroupType
      );

      if (selectedPrompt) {
        const { tutorGoal, tutorSummary } = getPromptLabelsObj({
          t,
          i18n,
          ...(selectedPrompt?.metadata || {}),
        });

        const selectedSubject = _.find(options, option => {
          const { children } = option || {};
          return _.includes(children, initialGroupType);
        })?.key;

        if (selectedSubject) {
          setActiveSubject(selectedSubject);
        }

        setSelectedPromptLocal(selectedPrompt);

        const childPromptId = _.get(selectedPrompt, "children.0");
        const childPrompt = _.find(flattenedPrompts, { id: childPromptId });

        if (childPrompt) {
          const label = wrapPlaceholderWithSpan({ label: tutorGoal });
          setSelectedChildPrompt({
            ...childPrompt,
            label: label,
            displayText: label,
            summary: tutorSummary,
          });
        }
      }
    } else {
      const firstSubject = _.find(options, option => option.key !== "root");
      if (firstSubject && !activeSubject) {
        setActiveSubject(firstSubject.key);
      }
    }
  }, [initialGroupType, flattenedPrompts, options, t, i18n, activeSubject]);

  useEffect(() => {
    const container = promptsContainerRef.current;
    if (!container) return;

    const handleScroll = _.throttle(() => {
      if (isSubjectClickRef.current) return;

      let selectedSection = null;
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top;
      const threshold = containerTop + containerRect.height * 0.2;

      const nonRootOptions = options.filter(option => option.key !== "root");

      for (const option of nonRootOptions) {
        const subjectId = option.key;
        const el = subjectSectionRefs.current[subjectId];

        if (!el) continue;

        const rect = el.getBoundingClientRect();
        const isVisible =
          rect.bottom > containerTop &&
          rect.top < containerTop + containerRect.height;

        if (isVisible) {
          if (rect.top <= threshold) {
            selectedSection = subjectId;
          } else {
            break;
          }
        }
      }

      if (!selectedSection) {
        for (const option of nonRootOptions) {
          const subjectId = option.key;
          const el = subjectSectionRefs.current[subjectId];

          if (!el) continue;

          const rect = el.getBoundingClientRect();
          const isVisible =
            rect.bottom > containerTop &&
            rect.top < containerTop + containerRect.height;

          if (isVisible) {
            selectedSection = subjectId;
            break;
          }
        }
      }

      if (selectedSection && selectedSection !== activeSubject) {
        setActiveSubject(selectedSection);
      }
    }, 100);

    container.addEventListener("scroll", handleScroll);

    setTimeout(() => {
      handleScroll();
    }, 200);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [activeSubject, options]);

  // ------------------ functions -----------------------

  const onClickSideNavigationOption = subjectId => {
    isSubjectClickRef.current = true;

    setActiveSubject(subjectId);
    setSelectedPrompt(null);
    setSelectedChildPrompt(null);

    const target = subjectSectionRefs.current[subjectId];
    const container = promptsContainerRef.current;

    if (target && container) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const scrollOffset =
        container.scrollTop + (targetRect.top - containerRect.top) - 16;

      container.scrollTo({
        top: scrollOffset,
        behavior: "smooth",
      });
    }

    setTimeout(() => {
      isSubjectClickRef.current = false;
    }, 800);
  };

  const setSelectedPromptLocal = prompt => {
    if (prompt?.id === selectedPrompt?.id) {
      setSelectedPrompt(null);
    } else {
      const childPrompt = _.map(prompt?.children, id =>
        _.find(flattenedPrompts, { id })
      );

      const children =
        _.get(childPrompt, "0.action") === "draft_ai_tutor"
          ? [prompt]
          : childPrompt;

      setSelectedPrompt({
        ...prompt,
        children,
      });
    }
  };

  const onClickUseThisTemplate = () => {
    if (!_.isEmpty(selectedChildPrompt)) {
      setSelectedAction(selectedChildPrompt);

      const modality = selectedPrompt?.metadata?.modality;

      if (_.isFunction(setSelectedLearningTool)) {
        if (modality) {
          const selectedTool = _.find(AI_TUTOR_LEARNING_TOOLS, {
            key: modality,
          });
          if (!_.isEmpty(selectedTool)) {
            setSelectedLearningTool({
              ...selectedTool,
              isAddedFromPrompt: true,
              shouldPassLearningToolToAI: true,
            });
          }
        } else {
          setSelectedLearningTool(null);
        }
      }
    }
    onClose();
  };

  //classes

  return (
    <Modal
      width={"1472px"}
      height={"932px"}
      isOpen={true}
      onClose={onClose}
      dsVersion={"2.0"}
      zIndex={1000}
      shouldCloseOnOverlayClick={false}
    >
      <Modal.Header
        title={t("ai:template_library")}
        isClosable={true}
        hasSeparator={true}
        style={styles.headerStyle}
      />

      <Modal.Body isScrollable={false} style={styles.modalBodyStyle}>
        {isLoading && !isData ? (
          <Loading />
        ) : (
          <div className={classes.bodyContainer}>
            <SideNavigation
              dsVersion={"2.0"}
              options={options}
              theme="light"
              selectedOptionKey={activeSubject}
              onClick={onClickSideNavigationOption}
              containerStyle={styles.sideNavigationStyle}
              hideRightBorder={true}
            />

            <div className={classes.promptsContainer} ref={promptsContainerRef}>
              {options
                .filter(option => option.key !== "root")
                .map(option => {
                  const subjectPrompts = _.compact(
                    option.children.map(id => _.find(prompts, { id }))
                  );

                  if (_.isEmpty(subjectPrompts)) return null;

                  return (
                    <div
                      key={option.key}
                      ref={ref => {
                        if (ref) {
                          subjectSectionRefs.current[option.key] = ref;
                        }
                      }}
                      className={classes.subjectSection}
                    >
                      <div
                        className={classNames(
                          "text-label-l",
                          classes.headerLabel
                        )}
                      >
                        {option.title?.props?.children || option.label}
                      </div>

                      <PromptCardsV2
                        prompts={subjectPrompts}
                        allPrompts={flattenedPrompts}
                        selectedPrompt={selectedPrompt}
                        setSelectedPrompt={setSelectedPromptLocal}
                        setSelectedAction={setSelectedChildPrompt}
                        leId={leId}
                        assessmentDetails={assessmentDetails}
                        gradeId={gradeId}
                        assessmentTemplate={assessmentTemplate}
                        source={source}
                        isAITutor={true}
                      />
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer hasSeparator={true}>
        <div className={classes.modalFooter}>
          <Button
            variant={"neutral"}
            type={"outlined"}
            size="medium"
            dsVersion={"2.0"}
            onClick={onClose}
          >
            {t("common:cancel")}
          </Button>

          <Button
            variant={"progressive"}
            type={"fill"}
            size="medium"
            dsVersion={"2.0"}
            onClick={onClickUseThisTemplate}
            disabled={_.isEmpty(selectedPrompt)}
          >
            {t("ai:use_this_template")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { assessmentDetails } = ownProps;
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);

  const userInfo = getUserInfo();
  const userId = _.get(userInfo, "id");

  const aiConversation = _.get(assessmentDetails, "aiConversation.edges", []);

  return {
    userId,
    aiConversation,
    curriculumProgramId,
    curriculumProgramType,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  I18nHOC({ resource: ["common", "ai", "aiTutor"] }),
  connect(mapStateToProps, null),
  graphql(getAIPromptsQuery, {
    name: "getAIPrompts",
    skip: ({ leId, entityType }) => !leId || !entityType,
    options: ({
      leId,
      entityType,
      portalType,
      curriculumProgramId,
      curriculumProgramType,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          filters: {
            entityId: leId,
            entityType,
            curriculumProgramId,
            categories: [
              {
                entityType: "CUSTOM",
                entities: curriculumProgramType,
              },
            ],
          },
          portalType,
        },
      };
    },
    props({
      getAIPrompts,
      ownProps: { isData, isLoading, t, i18n, ...props },
    }) {
      const queryData = _.get(getAIPrompts, "ai", {});

      const prompts = _.get(queryData, "prompts", []);
      const networkStatus = _.get(getAIPrompts, "networkStatus");

      const { flattenedPrompts } = getPromptsForSuggestionLibrary({
        ...props,
        prompts,
      });

      const displayPrompts = getIsAITutorInEEEnabled()
        ? _.filter(flattenedPrompts, {
            promptType: "display",
          })
        : _.filter(
            flattenedPrompts,
            prompt =>
              prompt?.action !== "extended_essay" &&
              prompt?.promptType === "display"
          );

      const options = getAITutorSidebarOptions({
        t,
        i18n,
        displayPrompts,
      });

      const initialOption = _.find(options, ({ key }) => key !== "root")?.key;

      return {
        prompts,
        options,
        initialOption,
        displayPrompts,
        flattenedPrompts,
        isData: isData && !_.isEmpty(queryData),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(AITutorLibraryV2);
